import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';

interface LanguageSelectorProps {
  className?: string;
  iconOnly?: boolean;
}

const LanguageSelector = ({ className, iconOnly = false }: LanguageSelectorProps) => {
  const { language, setLanguage } = useLanguage();
  const { translate } = useTranslation();
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size={iconOnly ? "icon" : "sm"}
          className={cn("relative", className)}
          aria-label={translate("common.selectLanguage")}
        >
          <Globe className="h-5 w-5" />
          <span className={cn(
            "flex items-center justify-center",
            iconOnly ? "absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground" : "ml-2"
          )}>
            {iconOnly ? language.toUpperCase() : languages.find(lang => lang.code === language)?.flag}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem 
            key={lang.code}
            className={cn(
              "cursor-pointer flex items-center gap-2",
              language === lang.code && "bg-secondary font-medium"
            )}
            onClick={() => setLanguage(lang.code as Language)}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
